<template>
  <div class="login-container">
    <!-- 背景装饰元素 -->
    <div class="bg-decoration">
      <div class="decoration-circle circle-1"></div>
      <div class="decoration-circle circle-2"></div>
      <div class="decoration-circle circle-3"></div>
      <div class="decoration-grid"></div>
    </div>

    <!-- 左侧信息区 -->
    <div class="login-left">
      <div class="left-content">
        <h1 class="system-title" v-html="systemTitle"></h1>
      </div>
    </div>

    <!-- 右侧登录表单区 -->
    <div class="login-right">
      <div class="login-card">
        <div class="card-header">
          <h2 class="welcome-title">欢迎登录</h2>
          <p class="welcome-subtitle">智慧社区统一运营管理平台</p>
        </div>

        <div v-if="localUiPreviewEnabled" class="preview-notice">
          <i class="el-icon-view"></i>
          <div><strong>本地 UI 预览模式</strong><span>无需验证码和后端服务，仅用于视觉验收</span></div>
        </div>

        <el-form :model="loginForm" :rules="rules" ref="loginForm" class="login-form">
          <el-form-item prop="username">
            <el-input
              v-model="loginForm.username"
              :placeholder="$t('login.usernamePlaceholder')"
              prefix-icon="el-icon-user"
              size="medium">
            </el-input>
          </el-form-item>
          <el-form-item prop="passwd">
            <el-input
              v-model="loginForm.passwd"
              type="password"
              :placeholder="$t('login.passwordPlaceholder')"
              prefix-icon="el-icon-lock"
              size="medium"
              @keyup.enter.native="submitForm('loginForm')">
            </el-input>
          </el-form-item>
          <el-form-item v-if="!localUiPreviewEnabled" prop="validateCode" class="captcha-item">
            <div class="captcha-container">
              <el-input
                v-model="loginForm.validateCode"
                :placeholder="$t('login.captchaPlaceholder')"
                prefix-icon="el-icon-picture"
                size="medium">
              </el-input>
              <div class="captcha-wrapper" @click="refreshCaptcha">
                <img :src="captchaUrl" class="captcha-img" alt="验证码">
                <div class="captcha-refresh">
                  <i class="el-icon-refresh"></i>
                </div>
              </div>
            </div>
          </el-form-item>
          <el-form-item prop="policyAccepted" class="policy-item">
            <div class="policy-confirmation">
              <el-checkbox v-model="loginForm.policyAccepted">我已阅读并同意</el-checkbox>
              <button type="button" class="policy-link" @click="openPolicy('agreement')">《用户协议》</button>
              <span class="policy-separator">和</span>
              <button type="button" class="policy-link" @click="openPolicy('privacy')">《隐私政策》</button>
            </div>
          </el-form-item>
          <el-form-item>
            <el-button
              v-if="localUiPreviewEnabled"
              type="primary"
              icon="el-icon-view"
              class="login-button"
              @click="enterLocalPreview">
              进入本地 UI 预览
            </el-button>
            <el-button
              v-else
              type="primary"
              @click="submitForm('loginForm')"
              :loading="loading"
              class="login-button">
              {{ loading ? '登录中...' : $t('login.login') }}
            </el-button>
          </el-form-item>
        </el-form>

        <div class="card-footer">
          <div class="copyright"> <span v-html="companyName"></span></div>
        </div>
      </div>
    </div>

    <select-login-user ref="selectLoginUserRef" @select="handleSelect"></select-login-user>

    <el-dialog :visible.sync="agreementVisible" width="640px" append-to-body class="policy-dialog"
      custom-class="policy-dialog-card">
      <div slot="title" class="policy-dialog-title">
        <span class="policy-dialog-icon"><i class="el-icon-document"></i></span>
        <span>用户协议</span>
      </div>
      <div class="policy-dialog-tip">请在使用系统前仔细阅读以下内容。</div>
      <div class="policy-content">{{ userAgreementContent }}</div>
      <span slot="footer">
        <el-button type="primary" @click="agreementVisible = false">我已了解</el-button>
      </span>
    </el-dialog>

    <el-dialog :visible.sync="privacyVisible" width="640px" append-to-body class="policy-dialog"
      custom-class="policy-dialog-card">
      <div slot="title" class="policy-dialog-title">
        <span class="policy-dialog-icon"><i class="el-icon-lock"></i></span>
        <span>隐私政策</span>
      </div>
      <div class="policy-dialog-tip">本说明介绍系统处理和保护相关信息的基本原则。</div>
      <div class="policy-content">{{ privacyPolicyContent }}</div>
      <span slot="footer">
        <el-button type="primary" @click="privacyVisible = false">我已了解</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { login, getCaptcha, _loadStaffPrivileges } from '@/api/user/loginApi';
import { _loadCommunityInfo } from '@/api/community/communityApi';
import {listSystemInfo} from '@/api/system/systemInfoManageApi'
import selectLoginUser from '@/components/user/selectLoginUser.vue';
import { createLocalPreviewSession, isLocalUiPreview } from '@/utils/localUiPreview';

const DEFAULT_USER_AGREEMENT = `欢迎使用本系统。

一、账号使用
用户应妥善保管账号和密码，仅在授权范围内使用本系统，不得转让、出借账号，也不得实施影响系统安全及正常运营的行为。

二、信息与操作
用户通过本系统提交、查询和处理的信息应当真实、合法，并遵守所在单位的管理制度及相关法律法规。通过用户账号完成的业务操作，将按照系统权限和审计规则进行记录。

三、系统使用
用户不得利用本系统从事未经授权的访问、数据处理或其他违法违规活动。继续使用本系统，即表示用户已阅读并同意本协议。`

const DEFAULT_PRIVACY_POLICY = `本系统重视用户信息与业务数据安全。

一、信息使用范围
本系统在登录及使用过程中，为完成身份认证、权限管理、业务办理、安全审计和故障排查，可能处理账号标识、联系方式、登录记录、操作记录，以及用户在授权范围内提交的业务信息。

二、信息保护
运营方将采取合理的访问控制、日志审计和安全防护措施保护相关信息，不会以出售个人信息为目的使用数据。依法需要或者经用户、所属单位授权时，可能在必要范围内提供相关信息。

三、用户权利
用户可以向运营方申请查询、更正或处理相关个人信息。具体适用范围、保存期限、联系方式及其他事项，以运营方正式发布并经审核确认的隐私政策为准。`

function validatePolicyAccepted(rule, value, callback) {
  if (value) {
    callback()
    return
  }
  callback(new Error('请先阅读并同意用户协议和隐私政策'))
}

export default {
  name: 'Login',
  data() {
    return {
      logo: '/img/logo_hc.png', // 默认logo
      companyName:'',
      systemTitle:'',
      loginForm: {
        username: '',
        passwd: '',
        validateCode: '',
        policyAccepted: false
      },
      captchaUrl: '',
      loading: false,
      agreementVisible: false,
      privacyVisible: false,
      userAgreementContent: DEFAULT_USER_AGREEMENT,
      privacyPolicyContent: DEFAULT_PRIVACY_POLICY,
      localUiPreviewEnabled: isLocalUiPreview,
      rules: {
        username: [
          { required: true, message: this.$t('login.usernamePlaceholder'), trigger: 'blur' }
        ],
        passwd: [
          { required: true, message: this.$t('login.passwordPlaceholder'), trigger: 'blur' }
        ],
        validateCode: [
          { required: true, message: this.$t('login.captchaPlaceholder'), trigger: 'blur' }
        ],
        policyAccepted: [
          { validator: validatePolicyAccepted, trigger: 'change' }
        ]
      }
    };
  },
  created() {
    localStorage.clear();
    if (this.localUiPreviewEnabled) {
      this.systemTitle = '智慧社区统一运营管理平台'
      this.companyName = '本地 UI 预览环境'
      return
    }
    this.getSystemInfo()
    setTimeout(() => {
      this.refreshCaptcha();
    }, 1000);

  },
  components: {
    selectLoginUser
  },
  methods: {
    enterLocalPreview() {
      if (!this.loginForm.policyAccepted) {
        this.$refs.loginForm.validateField('policyAccepted')
        return
      }
      createLocalPreviewSession()
      this.$router.push('/views/index/index')
    },
    openPolicy(type) {
      if (type === 'agreement') {
        this.agreementVisible = true
        return
      }
      this.privacyVisible = true
    },
    async getSystemInfo() {
      const { data } = await listSystemInfo({ page: 1, row: 1 });
      // 如果后端返回了logoUrl则使用，否则使用默认logo
      this.logo = data[0].logoUrl || '/img/logo_hc.png';
      this.companyName = data[0].companyName;
      this.systemTitle = data[0].systemTitle
    },
    async submitForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          this.loading = true;
          try {
            const credentials = {
              username: this.loginForm.username,
              passwd: this.loginForm.passwd,
              validateCode: this.loginForm.validateCode
            }
            const { data } = await login(credentials).catch(error => {
              this.$message.error(error);
              this.refreshCaptcha();
              return { data: [] };
            });
            if (!data || data.length == 0) {
              return;
            }

            if (data && data.length > 1) {
              this.$refs.selectLoginUserRef.openDialog(data);
              return;
            }
            this.handleSelect(data[0]);
          } catch (error) {
            console.error('登录失败：', error);
            this.refreshCaptcha();
          } finally {
            this.loading = false;
          }
        } else {
          return false;
        }
      });
    },
    async handleSelect(_userData) {
      localStorage.setItem('token', _userData.token);
      let _user = {
        userId: _userData.userId,
        name: _userData.name,
        tel: _userData.tel
      }
      localStorage.setItem('user', JSON.stringify(_user));
      await _loadStaffPrivileges();
      await _loadCommunityInfo();
      this.$router.push('/');
    },
    async refreshCaptcha() {
      try {
        const res = await getCaptcha();
        console.log(res);
        this.captchaUrl = res;
      } catch (error) {
        console.error('获取验证码失败：', error);
        this.$message.error(this.$t('login.getCaptchaFailed'));
      }
    }
  }
};
</script>

<style scoped>
.login-container {
  display: flex;
  height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0f2347 0%, #172554 55%, #0b1b38 100%);
}

/* 背景装饰 */
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(14, 165, 233, 0.05) 100%);
  animation: float 20s infinite ease-in-out;
}

.circle-1 {
  width: 400px;
  height: 400px;
  top: -150px;
  left: -150px;
  animation-delay: 0s;
}

.circle-2 {
  width: 300px;
  height: 300px;
  bottom: -100px;
  right: -100px;
  animation-delay: 5s;
}

.circle-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  right: 10%;
  animation-delay: 10s;
}

.decoration-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(rgba(59, 130, 246, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.035) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.5;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

/* 左侧信息区 */
.login-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  position: relative;
  z-index: 1;
  background-image: url('/img/login-smart-community-v3.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
}

.login-left::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(15, 35, 71, 0.12) 0%, rgba(15, 35, 71, 0.32) 100%);
  z-index: 1;
}

.left-content {
  width: 100%;
  max-width: 760px;
  color: #fff;
  position: relative;
  z-index: 2;
  text-align: center;
}

.system-logo {
  margin-bottom: 30px;
  text-align: center;
}

.system-logo img {
  max-width: 120px;
  max-height: 120px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.system-title {
  font-size: clamp(38px, 3.5vw, 54px);
  font-weight: 700;
  margin: 0;
  color: #fff;
  text-align: center;
  letter-spacing: 3px;
  position: relative;
  z-index: 2;
  text-shadow: 0 3px 16px rgba(3, 14, 32, 0.55), 0 0 24px rgba(59, 130, 246, 0.24);
  line-height: 1.4;
}

.system-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin: 0 0 50px 0;
  font-weight: 300;
  letter-spacing: 2px;
}

.feature-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 40px;
}

.feature-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.feature-item:hover {
  background: rgba(62, 119, 252, 0.2);
  border-color: rgba(62, 119, 252, 0.5);
  transform: translateY(-2px);
}

.feature-item i {
  font-size: 24px;
  color: #3E77FC;
  margin-right: 12px;
}

.feature-item span {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
}

/* 右侧登录表单区 */
.login-right {
  flex: 0 0 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  z-index: 1;
  background: linear-gradient(180deg, rgba(15, 35, 71, 0.94) 0%, rgba(11, 27, 56, 0.98) 100%);
  backdrop-filter: blur(12px);
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.88);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 24px 64px rgba(3, 14, 32, 0.32);
  animation: slideIn 0.5s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.card-header {
  text-align: center;
  margin-bottom: 35px;
}

.welcome-title {
  font-size: 28px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.welcome-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  font-weight: 400;
}

.preview-notice {
  display: flex;
  align-items: center;
  margin: -12px 0 22px;
  padding: 12px 14px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  color: #1d4ed8;
  background: #eff6ff;
  text-align: left;
}

.preview-notice > i {
  margin-right: 10px;
  font-size: 20px;
}

.preview-notice strong,
.preview-notice span {
  display: block;
}

.preview-notice strong {
  font-size: 13px;
}

.preview-notice span {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
}

.login-form {
  margin-top: 30px;
}

.login-form .el-form-item {
  margin-bottom: 24px;
}

.login-form .policy-item {
  margin-top: -4px;
  margin-bottom: 18px;
}

.policy-confirmation {
  display: flex;
  align-items: center;
  min-height: 24px;
  color: #64748b;
  font-size: 13px;
  line-height: 20px;
  text-align: left;
  white-space: nowrap;
}

.policy-confirmation :deep(.el-checkbox) {
  margin-right: 2px;
}

.policy-confirmation :deep(.el-checkbox__label) {
  padding-left: 6px;
  color: #64748b;
  font-size: 13px;
}

.policy-link {
  margin: 0;
  padding: 0;
  color: #2563eb;
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
}

.policy-link:hover {
  color: #3b82f6;
  text-decoration: underline;
}

.policy-separator {
  margin: 0 2px;
}

.policy-content {
  max-height: 48vh;
  overflow-y: auto;
  padding: 18px 20px;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 14px;
  line-height: 1.9;
  text-align: left;
  white-space: pre-wrap;
}

.policy-dialog-title {
  display: flex;
  align-items: center;
  color: #1e293b;
  font-size: 17px;
  font-weight: 600;
}

.policy-dialog-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 10px;
  color: #2563eb;
  border-radius: 8px;
  background: #eff6ff;
  font-size: 16px;
}

.policy-dialog-tip {
  margin-bottom: 14px;
  padding: 10px 12px;
  color: #64748b;
  border-left: 3px solid #2563eb;
  border-radius: 0 6px 6px 0;
  background: #f8fafc;
  font-size: 13px;
  line-height: 1.6;
  text-align: left;
}

.login-form :deep(.el-input__inner) {
  height: 48px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 14px;
  transition: all 0.3s;
}

.login-form :deep(.el-input__inner):focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.login-form :deep(.el-input__prefix) {
  left: 15px;
  color: #94a3b8;
}

.login-form :deep(.el-input--prefix .el-input__inner) {
  padding-left: 45px;
}

.captcha-container {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.captcha-item :deep(.el-input) {
  flex: 1;
}

.captcha-wrapper {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.3s;
}

.captcha-wrapper:hover {
  border-color: #2563eb;
}

.captcha-img {
  display: block;
  height: 48px;
  width: 120px;
  object-fit: cover;
}

.captcha-refresh {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.captcha-wrapper:hover .captcha-refresh {
  opacity: 1;
}

.captcha-refresh i {
  color: #fff;
  font-size: 18px;
}

.login-button {
  width: 100%;
  height: 48px;
  background: #2563eb;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.24);
}

.login-button:hover {
  transform: translateY(-2px);
  background: #3b82f6;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3);
}

.login-button:active {
  transform: translateY(0);
}

.card-footer {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.copyright {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.6;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .login-container {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }

  .login-left {
    flex: 0 0 auto;
    padding: 40px 30px;
  }

  .login-right {
    flex: 1;
    width: 100%;
    padding: 20px;
  }

  .system-title {
    font-size: 32px;
    letter-spacing: 2px;
  }

  .feature-list {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}

@media (max-width: 768px) {
  .login-card {
    padding: 30px 25px;
  }

  .welcome-title {
    font-size: 24px;
  }

  .system-title {
    font-size: 28px;
    letter-spacing: 2px;
  }

  .system-subtitle {
    font-size: 14px;
  }

  .login-left {
    padding: 40px 20px;
  }
}
</style>
